import {useState} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {useToast} from '../Toats'
import {activateNuklie, disconnectNuklie} from '../../../services/nuklieService'
import {DateComplete} from '../../../utils/date'
import {NUKLIE_STATUS_QUERY, useNuklie} from './NuklieProvider'
import {NUKLIE_ICON} from './constants'

const errorMessage = (error: any, fallback: string) => error?.response?.data?.error || fallback

/**
 * Linha "Nuklie" em Conexões Externas: troca o código de ativação gerado na Nuklie
 * (Configurar → Acesso externo) pela credencial, guardada só no servidor.
 */
export function NuklieActivation() {
  const {status, canManage} = useNuklie()
  const queryClient = useQueryClient()
  const {toggleToast} = useToast()
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)

  if (!canManage) return null

  const refresh = () => queryClient.invalidateQueries({queryKey: NUKLIE_STATUS_QUERY})

  const activate = async () => {
    if (!code.trim()) {
      toggleToast('Informe o código de ativação da Nuklie.', 'error')
      return
    }
    try {
      setLoading(true)
      await activateNuklie(code.trim())
      setCode('')
      toggleToast('Atendimento Nuklie ativado.', 'success')
      await refresh()
    } catch (error) {
      toggleToast(errorMessage(error, 'Erro ao ativar o código da Nuklie.'), 'error')
    } finally {
      setLoading(false)
    }
  }

  const disconnect = async () => {
    const confirmed = window.confirm(
      'Desconectar o Atendimento Nuklie desta empresa? Para ativar de novo, revogue o acesso anterior na Nuklie (Configurar → Acesso externo) e gere um novo código.'
    )
    if (!confirmed) return
    try {
      setLoading(true)
      await disconnectNuklie()
      toggleToast('Atendimento Nuklie desconectado.', 'success')
      await refresh()
    } catch (error) {
      toggleToast(errorMessage(error, 'Erro ao desconectar a Nuklie.'), 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='row mb-6'>
      <div className='col-lg-4 col-form-label d-flex align-items-center gap-3'>
        <img src={NUKLIE_ICON} alt='' width={40} />
        <span className='fw-bolder fs-4 text-gray-900'>Nuklie</span>
      </div>

      <div className='col-lg-8 fv-row'>
        {status?.active ? (
          <div className='d-flex flex-wrap align-items-center justify-content-between gap-3'>
            <div>
              <span className='badge badge-light-success fs-7 fw-bold me-2'>Ativo</span>
              <span className='text-gray-700 fs-6'>
                Atendimento embutido
                {status.activated_at ? ` desde ${DateComplete(new Date(status.activated_at))}` : ''}
              </span>
              <div className='text-muted fs-7 mt-1 font-monospace'>
                {status.integration_public_identifier}
              </div>
            </div>
            <button className='btn btn-light-danger btn-sm' onClick={disconnect} disabled={loading}>
              {loading ? 'Aguarde...' : 'Desconectar'}
            </button>
          </div>
        ) : (
          <>
            <div className='d-flex gap-2'>
              <input
                type='text'
                className='form-control form-control-lg form-control-solid font-monospace'
                placeholder='Código de ativação (NKL-XXXX-XXXX-XXXX)'
                value={code}
                onChange={(event) => setCode(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && activate()}
                autoComplete='off'
              />
              <button className='btn btn-primary text-nowrap' onClick={activate} disabled={loading}>
                {!loading && 'Ativar'}
                {loading && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    Aguarde...{' '}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
            <div className='text-muted fs-7 mt-2'>
              Gere o código na Nuklie em Configurar → Acesso externo. Ele vale 15 minutos e serve uma
              única vez.
            </div>
          </>
        )}
      </div>
    </div>
  )
}
